package fr.frux.feature.treeadd.wizard

import fr.frux.core.common.logging.Logger
import fr.frux.core.media.PickedImage
import fr.frux.core.media.PreparedPhoto
import fr.frux.shared.content.DescriptionLength
import fr.frux.shared.domain.model.TreeStatus
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlin.uuid.Uuid

private const val TAG = "DetailsStepController"

/**
 * États proposés à l'étape Détails. `UNAVAILABLE` n'a pas de sens à la création (on ne signale
 * pas un arbre indisponible en le créant), `UNKNOWN` est l'absence de choix.
 */
val WIZARD_STATUSES: List<TreeStatus> =
    listOf(TreeStatus.FLOWERING, TreeStatus.RIPE_READY, TreeStatus.PICKED_CLEAN)

/**
 * Logique de l'étape Détails (état du jour facultatif, photos, note), extraite de
 * [TreeWizardViewModel] (detekt `TooManyFunctions`) : le ViewModel l'expose en `details`, les
 * écrans l'appellent directement. [scope] est son `viewModelScope`.
 */
class DetailsStepController internal constructor(
    private val updateState: ((TreeWizardState) -> TreeWizardState) -> Unit,
    private val scope: CoroutineScope,
    private val preparePhoto: suspend (PickedImage) -> PreparedPhoto,
) {
    /** Un second appui sur l'état déjà choisi le décoche : l'état est facultatif. */
    fun toggleStatus(status: TreeStatus) {
        if (status !in WIZARD_STATUSES) return
        updateState { s ->
            s.withDetails { it.copy(status = if (it.status == status) null else status) }
        }
    }

    /**
     * `null` : sélecteur annulé. Au-delà de [MAX_WIZARD_PHOTOS] la photo est ignorée. La photo
     * apparaît aussitôt, sa version préparée arrive ensuite dans [WizardPhoto.prepared].
     */
    fun addPhoto(image: PickedImage?) {
        if (image == null) return
        val photo = WizardPhoto(id = Uuid.random().toString(), image = image, prepared = null)
        var accepted = false
        updateState { s ->
            accepted = s.details.canAddPhoto
            if (accepted) s.withDetails { it.copy(photos = it.photos + photo) } else s
        }
        if (accepted) prepare(photo)
    }

    fun removePhoto(id: String) {
        updateState { s -> s.withDetails { d -> d.copy(photos = d.photos.filterNot { it.id == id }) } }
    }

    fun onNoteChange(note: String) {
        updateState { s -> s.withDetails { it.copy(note = note.truncatedTo(DescriptionLength.MAX)) } }
    }

    private fun prepare(photo: WizardPhoto) {
        scope.launch {
            val prepared =
                try {
                    preparePhoto(photo.image)
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (
                    @Suppress("TooGenericExceptionCaught") error: Exception,
                ) {
                    // Sans version préparée, l'envoi retombera sur l'image d'origine.
                    Logger.e(TAG, "Préparation de la photo impossible", error)
                    return@launch
                }
            updateState { s ->
                s.withDetails { d ->
                    val photos = d.photos.map { if (it.id == photo.id) WizardPhoto(it.id, it.image, prepared) else it }
                    d.copy(photos = photos)
                }
            }
        }
    }
}

/** Tronque sans couper une paire de substituts en deux. */
private fun String.truncatedTo(max: Int): String {
    if (length <= max) return this
    val cut = if (this[max - 1].isHighSurrogate()) max - 1 else max
    return substring(0, cut)
}

private inline fun TreeWizardState.withDetails(transform: (DetailsStepState) -> DetailsStepState): TreeWizardState =
    copy(details = transform(details))
